import Page from '@/gf/components/Page'
import Frame from '../components/Frame'
import useToggle from '@/gf/hooks/useToggle'
import UploadPartsManual from '../components/UploadPartsManual'
import { BookOpenIcon } from '@heroicons/react/24/outline'
import Action from '@/gf/components/Action'
import Pagination from '@/gf/components/Pagination'

import useGqlClient from '../hooks/useGqlClient'
import useSession from '../hooks/useSession'
import usePage from '@/gf/hooks/usePage'
import Card from '@/gf/components/Card'
import { Td, Th, Tr } from '@/gf/components/Table'
import Link from '@/gf/components/Link'
import TextTag from '@/gf/components/next/TextTag'
import { ManualState, useListManualsQuery } from '../_gen/gql'
import useModal from '@/gf/hooks/useModal'
import AssignOrgMachinesModal from './KnowledgeCenter/AssignOrgMachinesModal'
import Id from '@/gf/modules/Id'
import pluralize from 'pluralize'
import Dropdown from '@/gf/components/Dropdown'

const ModelsCell = ({ models }: { models: string[] }) => {
  if (models.length <= 1) {
    return <span>{models.join(', ')}</span>
  }

  const [firstModel, ...otherModels] = models

  return (
    <Dropdown
      title={
        <div className="flex gap-2 text-left">
          <span className="truncate max-w-48">{firstModel}</span>
          <div className="flex-shrink-0">
            <span className="font-medium text-xs bg-slate-100 text-slate-500 rounded-full px-2 py-1">
              +{otherModels.length}
            </span>
          </div>
        </div>
      }
      stopEventPropagation
      className="hover:bg-blue-50 px-0 py-0"
      chevronClassName="opacity-100"
    >
      <div className="flex flex-col gap-y-2 max-w-64">
        {otherModels.map((model, index) => (
          <div key={index} className="truncate py-1">
            {model}
          </div>
        ))}
      </div>
    </Dropdown>
  )
}

const SerialNumbersCell = ({ serialNumbers }: { serialNumbers: string[] }) => {
  if (serialNumbers.length <= 1) {
    return <span>{serialNumbers.join(', ')}</span>
  }

  const [firstSerialNumber, ...otherSerialNumbers] = serialNumbers

  return (
    <Dropdown
      title={
        <div className="flex gap-2 text-left">
          <span className="truncate max-w-48">{firstSerialNumber}</span>
          <div className="flex-shrink-0">
            <span className="font-medium text-xs bg-slate-100 text-slate-500 rounded-full px-2 py-1">
              +{otherSerialNumbers.length}
            </span>
          </div>
        </div>
      }
      stopEventPropagation
      className="hover:bg-blue-50 px-0 py-0"
      chevronClassName="opacity-100"
    >
      <div className="flex flex-col gap-y-2 max-w-64">
        {otherSerialNumbers.map((serialNumber, index) => (
          <div key={index} className="truncate py-1">
            {serialNumber}
          </div>
        ))}
      </div>
    </Dropdown>
  )
}

const KnowledgeCenter = () => {
  const client = useGqlClient()
  const { orgId } = useSession()
  const [page, setPage] = usePage()
  const { data } = useListManualsQuery({ client, variables: { orgId, page } })
  const [uploadPartsManualOpen, uploadPartsManualToggle] = useToggle()
  const assignMachinesModal = useModal<{ partsManualId: string }>()

  if (!data) {
    return null
  }

  const partsManuals = data.manuals.entries

  return (
    <Frame
      breadcrumbs={{
        copy: 'Back to Dashboard',
        crumbs: [{ name: 'Knowledge Center', href: '/knowledge-center' }],
      }}
    >
      <UploadPartsManual open={uploadPartsManualOpen} onClose={uploadPartsManualToggle.off} />
      {assignMachinesModal.props?.partsManualId && (
        <AssignOrgMachinesModal
          open={assignMachinesModal.isOpen}
          onClose={assignMachinesModal.close}
          partsManualId={assignMachinesModal.props?.partsManualId}
        />
      )}

      <Page
        title="Knowledge Center"
        actionsNext={[
          <Action.P
            onClick={uploadPartsManualToggle.on}
            className="items-center flex gap-x-2"
            color="blue"
            key="upload-parts-manual-button"
          >
            <BookOpenIcon className="inline-block h-4 w-4" />
            Upload Parts Manual
          </Action.P>,
        ]}
      >
        <Card className="overflow-auto mt-6">
          <table className="min-w-full divide-y-1 divide-gray-200">
            <thead className="bg-gray-50 sticky top-0">
              <Tr>
                <Th>State</Th>
                <Th>Id</Th>
                <Th>Brand</Th>
                <Th>Models</Th>
                <Th></Th>
                <Th>Serial Numbers</Th>
                <Th>Equipment Type</Th>
                <Th>Filename</Th>
                <Th>Publication Date</Th>
              </Tr>
            </thead>
            <tbody className="divide-y-1 divide-gray-200">
              {partsManuals.length === 0 ? (
                <Tr>
                  <Td colSpan={9}>No manuals found.</Td>
                </Tr>
              ) : (
                partsManuals.map((manual) => (
                  <Tr key={manual.id}>
                    <Td className="capitalize">
                      <TextTag
                        color={
                          manual.state === ManualState.Queued
                            ? 'gray'
                            : manual.state === ManualState.Reading
                              ? 'blue'
                              : manual.state === ManualState.Extracting
                                ? 'orange'
                                : manual.state === ManualState.Ready
                                  ? 'green'
                                  : 'red'
                        }
                      >
                        {manual.state.toLocaleLowerCase()}
                      </TextTag>
                    </Td>
                    <Td>
                      <Link.T to={`/parts-manual/${manual.id}`}>{Id.shorten(manual.id)}</Link.T>
                    </Td>
                    <Td>{manual.brand}</Td>
                    <Td>
                      <ModelsCell models={manual.models} />
                    </Td>
                    <Td>
                      {manual.orgMachines.length > 0 && (
                        <span className="text-sm italic text-gray-500 block">
                          {manual.orgMachines.length}{' '}
                          {pluralize('machine', manual.orgMachines.length)}
                        </span>
                      )}

                      <Action.T
                        onClick={() => assignMachinesModal.open({ partsManualId: manual.id })}
                        className="block"
                      >
                        Link Machines
                      </Action.T>
                    </Td>
                    <Td>
                      <SerialNumbersCell serialNumbers={manual.serialNumbers} />
                    </Td>
                    <Td>{manual.equipmentType}</Td>
                    <Td>
                      {manual.filename && manual.url ? (
                        <a
                          href={manual.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 hover:underline truncate block max-w-xs"
                          title={manual.filename}
                        >
                          {manual.filename}
                        </a>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </Td>
                    <Td>{manual.publicationDate}</Td>
                  </Tr>
                ))
              )}
            </tbody>
          </table>
        </Card>

        <div className="mt-4">
          <Pagination pagination={data.manuals?.pagination} page={page} updatePage={setPage} />
        </div>
      </Page>
    </Frame>
  )
}

export default KnowledgeCenter
